/**
 * The configured API client. The generated code under `./generated` is never hand-edited.
 *
 * Everything there comes from `openapi.json` via `npm run generate:api`, and
 * `ops/dump_openapi.py --check` fails CI if that schema drifts from the service. So this
 * module is the only place the frontend states anything about *how* to call the API, as
 * opposed to *what* the API is.
 *
 * **Config goes through the constructor, not through the exported `OpenAPI` object.** The
 * generator was given `--name JmtApi`, which produces a client *class* whose services read
 * the config passed to `new JmtApi(...)`. The `OpenAPI` global is still exported and is
 * still writable, and setting it has **no effect at all** on an instance — the first draft
 * of this file did exactly that, and every request would have gone out same-origin-by-luck
 * with no credentials and no CSRF header. Worth knowing because the mistake type-checks.
 *
 * ## The three rules the backend enforces, restated here because the client must obey them
 *
 * **The session is an `HttpOnly` cookie, so JavaScript cannot read it and must not try.**
 * `WITH_CREDENTIALS` is what makes the browser attach it. Without that flag every request
 * after login is a 401 and the network tab shows no cookie, which reads as a server bug.
 *
 * **CSRF is double-submit on every mutating request** (`jmt_api/deps.py`). The token is in
 * a readable cookie and has to be echoed in `X-CSRF-Token`; a page from another origin
 * cannot read the cookie, which is the whole mechanism. `/auth/login` and `/auth/mfa` are
 * exempt *by path* on the server, and this sends the header whenever it has one rather
 * than reimplementing that exemption list — a client-side copy of a server-side rule is
 * two rules that will disagree.
 *
 * **Money is a string** (H6). `DecimalStr` in `jmt_api/fields.py` declares `string` in the
 * schema, so the generated models type every price, size and tick as `string`. Do not
 * `Number()` one. When arithmetic is needed — task 31 onward — it needs `decimal.js`,
 * which `Tooling-and-Libraries.md` marks **Required** for exactly this reason.
 */

import { JmtApi } from './generated';

/** Name of the readable CSRF cookie, matching `ApiSettings.csrf_cookie_name`. */
export const CSRF_COOKIE = 'jmt_csrf';

/** Header the double-submit token travels in, matching `jmt_api.deps.CSRF_HEADER`. */
export const CSRF_HEADER = 'X-CSRF-Token';

export function readCookie(name: string): string | undefined {
  // `document.cookie` is the only way to read it, and it is only readable because the CSRF
  // cookie is deliberately not HttpOnly. The session cookie is, and is absent from this
  // string entirely — if it ever appears here, that is a server misconfiguration.
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return undefined;
}

/**
 * One client for the application.
 *
 * `BASE: ''` is same-origin. In development Vite proxies `/api` to the service (see
 * `vite.config.ts`) and in production Caddy serves both from one host, so the session
 * cookie's `SameSite=Strict` is satisfied in both. An absolute base URL would make every
 * request cross-origin and the browser would withhold the cookie without saying so.
 */
export const api = new JmtApi({
  BASE: '',
  WITH_CREDENTIALS: true,
  CREDENTIALS: 'same-origin',
  HEADERS: async (): Promise<Record<string, string>> => {
    const token = readCookie(CSRF_COOKIE);
    // Built as a record rather than returned from a ternary, because the generated
    // `Headers` type is `Record<string, string>` and an inferred `{ 'X-CSRF-Token'?:
    // undefined }` does not satisfy it.
    const headers: Record<string, string> = {};
    if (token) headers[CSRF_HEADER] = token;
    return headers;
  },
});
